import { Browser } from "@capacitor/browser";
import { createClient } from "@/lib/supabase/client";
import { sanitizeReturnUrl } from "@/lib/oauth/return-url";
import { takeCrewHandoffNonce } from "@/lib/crew/auth-handoff-client";
import { isHomePath } from "@/lib/crew/app-handoff";
import { LOCALE_PATTERN } from "@/i18n/routing";

const LOCALE_PREFIX = new RegExp(`^/(${LOCALE_PATTERN})(?:/|$|\\?|#)`);

/**
 * 로그인 직후 post-signup·전화 인증 화면에 쓸 **앱의** 로케일.
 *
 * 크루 핸드오프가 홈 next 를 "이어갈 경로"로 바꿀 수 있는데, 그 경로는 Safari 의 설치
 * 페이지(Accept-Language)에서 왔다 — 앱의 언어가 아니다. 그래서 로케일은 바꾸기 **전**의
 * 로그인 next 에서, 거기에 없으면 지금 앱 화면의 경로에서 읽는다. 둘 다 없으면 ko.
 */
export function resolveAppLocale(
  originalNext: string | null | undefined,
  currentPath: string | null | undefined,
): string {
  return (
    originalNext?.match(LOCALE_PREFIX)?.[1] ||
    currentPath?.match(LOCALE_PREFIX)?.[1] ||
    "ko"
  );
}

/**
 * OAuth 딥링크 처리.
 *
 * 포맷: locarora://auth/callback?next=/path&needs_phone=1#access_token=...&refresh_token=...
 *
 * 토큰은 보안상 반드시 해시(#)에, 플로우 플래그는 쿼리(?)에 옴.
 *
 * setSession 성공 후 nativeOAuthPostSignup 을 호출해 web /[locale]/auth/callback
 * 와 동일한 post-signup 책임 (geo, notification, ban check, phone self-heal)
 * 을 수행한다. Apple/Google/Kakao native flow 는 server callback 을 거치지
 * 않아서 이걸 명시적으로 호출 안 하면 모두 누락됨. Naver/LINE 은 이미 server
 * callback 에서 같은 일을 한 상태지만 모든 호출이 idempotent 라서 안전.
 *
 * 반환: 처리했으면 true (호출자는 일반 딥링크 라우팅을 건너뛰어야 함).
 */
export async function processAuthDeepLink(urlStr: string): Promise<boolean> {
  let url: URL;
  try {
    url = new URL(urlStr);
  } catch {
    return false;
  }

  if (url.protocol !== "locarora:" || url.host !== "auth") return false;

  const query = url.searchParams;
  const hash = new URLSearchParams(url.hash.replace(/^#/, ""));

  const accessToken = hash.get("access_token") || query.get("access_token");
  const refreshToken = hash.get("refresh_token") || query.get("refresh_token");
  let next = sanitizeReturnUrl(query.get("next"));
  const errorCode = query.get("error");
  const urlNeedsPhone = query.get("needs_phone") === "1";
  const blocked = query.get("blocked") === "1";

  // implicit flow 토큰이 hash 로 와야 하는데 비어 있으면 사용자는 무흔적 로그인 실패를
  // 겪는다 (Supabase Redirect URL wildcard 누락 시 web 으로 fallback 되는 케이스).
  // 콘솔에 단서를 남겨 Safari Web Inspector / Sentry 로 추적 가능하게 한다.
  console.info("[OAuth] callback received", {
    hasAccessToken: !!accessToken,
    hasRefreshToken: !!refreshToken,
    next,
    error: errorCode,
    urlNeedsPhone,
    blocked,
  });

  try {
    await Browser.close();
  } catch {
    // Android에서 Browser.close가 실패해도 UX에 영향 없음
  }

  if (errorCode) {
    // Blocked errors (Naver/LINE server callback) carry the destination — which
    // may already include a ?p=withdrawn presentation flag — in `next`. Route to
    // it verbatim instead of appending ?error= (which would double the query).
    if (errorCode === "blocked") {
      window.location.href = next;
      return true;
    }
    window.location.href = `${next}?error=${encodeURIComponent(errorCode)}`;
    return true;
  }

  if (blocked) {
    window.location.href = `/ko/blocked`;
    return true;
  }

  if (accessToken && refreshToken) {
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
      if (error) {
        console.error("setSession failed:", error);
        window.location.href = `/?error=session_failed`;
        return true;
      }
    } catch (err) {
      console.error("Failed to apply native OAuth session:", err);
      window.location.href = `/?error=session_failed`;
      return true;
    }

    // 크루 클릭 핸드오프 회수 — 로그인 창(시스템 브라우저)이 본 크루 클릭을 나에게 새긴다
    // (native-oauth.ts 주석). 세션이 선 뒤라야 서버 액션이 나를 안다. 실패는 무시한다.
    // 핸드오프가 기억한 "이어갈 경로"는 로그인 next 가 홈일 때만 쓴다 — 예약 중 로그인처럼
    // 흐름이 진행 중이면 그 흐름이 이긴다.
    // 로케일은 핸드오프가 next 를 바꾸기 전에 정한다(resolveAppLocale 주석).
    const locale = resolveAppLocale(next, window.location.pathname);
    const handoffNonce = takeCrewHandoffNonce();
    if (handoffNonce) {
      try {
        // 동적 import — 아래 nativeOAuthPostSignup 과 같은 이유(Turbopack 액션 프록시).
        const { claimCrewHandoff } = await import("@/app/actions/crew-handoff");
        const handoff = await claimCrewHandoff(handoffNonce);
        if (handoff.next && isHomePath(next)) next = handoff.next;
      } catch (err) {
        console.warn("[OAuth] crew handoff claim failed (non-fatal):", err);
      }
    }

    // Post-signup 책임 일괄 처리 — web callback route 와 동일.
    // 실패해도 redirect 는 진행 (client setSession 은 이미 성공이라 사용자가
    // 로그인 상태로 도착하는 게 더 좋음 — metadata 일부 누락은 다음 로그인에
    // 자가 치유됨).
    try {
      // 동적 import: 이 모듈은 root [locale]/layout 의 CapacitorBootstrap(client)
      // 에서 eager 평가된다. 서버 액션을 정적 import 하면 Turbopack 16.2.x dev 에서
      // 액션 프록시(data: 모듈)가 "module factory is not available" 로 깨진다.
      // 액션은 native 딥링크가 실제 도착했을 때만 필요하므로 호출 시점에 lazy 로드.
      const { nativeOAuthPostSignup } = await import(
        "@/app/actions/native-oauth-post-signup"
      );
      const result = await nativeOAuthPostSignup({ locale });
      if (result.blocked) {
        const suffix = result.presentation === "withdrawn" ? "?p=withdrawn" : "";
        window.location.href = `/${locale}/blocked${suffix}`;
        return true;
      }
      if (result.needsPhone) {
        window.location.href = `/${locale}/verify-phone?returnTo=${encodeURIComponent(next)}`;
        return true;
      }
    } catch (err) {
      console.error("[OAuth] post-signup action failed (non-fatal):", err);
    }

    // URL-embedded needs_phone (Naver/LINE server callback 가 명시한 경우)
    if (urlNeedsPhone) {
      window.location.href = `/${locale}/verify-phone?returnTo=${encodeURIComponent(next)}`;
    } else {
      window.location.href = next;
    }
    return true;
  }

  return false;
}
